// Epoch 53: CSR Record Outreach Usage Page
// Allows CSRs to record outreach usage for assigned company users
// Date: October 28, 2025

import React, { useEffect, useState } from 'react';
import { supabase } from '../../supabase/client';
import { useNavigate } from 'react-router-dom';
import CampaignSearchSelect, { CampaignOption } from '../../components/common/CampaignSearchSelect';

interface User {
  id: string;
  email: string;
  full_name: string | null;
  company_id: string;
  outreach_credits_balance: number;
  pricing_plan: string | null;
  billing_status: string | null;
  companies: {
    name: string;
  };
}

// Epoch 75: Campaign now includes target_list_name (use CampaignOption from shared component)
type Campaign = CampaignOption;

const CSRRecordUsage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<User[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [selectedCampaignId, setSelectedCampaignId] = useState<string>('');
  const [outreachesUsed, setOutreachesUsed] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Fetch assigned company users and their campaigns
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) {
          navigate('/login');
          return;
        }

        // Check if user is CSR
        const { data: csrData, error: csrError } = await supabase
          .from('users')
          .select('role')
          .eq('id', session.user.id)
          .single();

        if (csrError || csrData.role !== 'csr') {
          navigate('/dashboard');
          return;
        }

        // Get CSR's assigned companies
        const { data: assignments, error: assignmentError } = await supabase
          .from('company_csrs')
          .select('company_id')
          .eq('csr_id', session.user.id);

        if (assignmentError) {
          console.error('Supabase assignments query error:', assignmentError);
          throw new Error(`Failed to fetch company assignments: ${assignmentError.message || JSON.stringify(assignmentError)}`);
        }

        const companyIds = assignments.map(a => a.company_id);
        console.log('CSR assigned company IDs:', companyIds);

        if (companyIds.length === 0) {
          setUsers([]);
          setLoading(false);
          return;
        }

        // Fetch users from assigned companies with Platform, usage-based, or trial pricing
        // Platform users ($30/month) purchase credits separately
        // Usage-based users (legacy) also use credit system
        // Trial users (pricing_plan = NULL) get 100 free credits
        const { data: usersData, error: usersError } = await supabase
          .from('users')
          .select(`
            id,
            email,
            full_name,
            company_id,
            outreach_credits_balance,
            pricing_plan,
            billing_status,
            companies!users_company_id_fkey (
              name
            )
          `)
          .in('company_id', companyIds)
          .or('pricing_plan.in.(platform,usage_based),pricing_plan.is.null')
          .order('full_name', { ascending: true });

        if (usersError) {
          console.error('Supabase users query error:', usersError);
          throw new Error(`Failed to fetch users: ${usersError.message || JSON.stringify(usersError)}`);
        }

        setUsers(usersData || []);

        // Epoch 75: Fetch campaigns with company name and target list for searchable dropdown
        const { data: campaignsData, error: campaignsError } = await supabase
          .from('campaigns')
          .select(`
            id,
            name,
            companies:company_id (
              name
            )
          `)
          .in('company_id', companyIds)
          .order('name', { ascending: true });

        if (campaignsError) {
          console.error('Error fetching campaigns:', campaignsError);
        }

        // Epoch 75: Separate query for preset_filter_requests (nested join unreliable with RLS)
        const campaignIds = (campaignsData || []).map((c: any) => c.id);
        let filterRequestMap: Record<string, string> = {};
        if (campaignIds.length > 0) {
          const { data: filterData } = await supabase
            .from('preset_filter_requests')
            .select('campaign_id, request_name')
            .in('campaign_id', campaignIds);
          (filterData || []).forEach((r: any) => {
            if (r.request_name) filterRequestMap[r.campaign_id] = r.request_name;
          });
        }

        const processedCampaigns: Campaign[] = (campaignsData || []).map((c: any) => ({
          id: c.id,
          name: c.name,
          company_name: c.companies?.name || undefined,
          target_list_name: filterRequestMap[c.id] || undefined,
        }));

        setCampaigns(processedCampaigns);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError(error instanceof Error ? error.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    // Validation
    if (!selectedUserId) {
      setError('Please select a user');
      return;
    }

    const outreaches = parseInt(outreachesUsed);
    if (isNaN(outreaches) || outreaches <= 0) {
      setError('Please enter a valid number of outreaches (greater than 0)');
      return;
    }

    if (outreaches > 1000) {
      setError('Cannot record more than 1000 outreaches in a single entry');
      return;
    }

    // Find selected user to check balance
    const selectedUser = users.find(u => u.id === selectedUserId);
    if (!selectedUser) {
      setError('Selected user not found');
      return;
    }

    if (selectedUser.outreach_credits_balance < outreaches) {
      setError(`User only has ${selectedUser.outreach_credits_balance} credits available (requested ${outreaches})`);
      return;
    }

    try {
      setSubmitting(true);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        throw new Error('Not authenticated');
      }

      // Call record-outreach-usage Edge Function
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/record-outreach-usage`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            user_id: selectedUserId,
            outreaches_used: outreaches,
            campaign_id: selectedCampaignId || null,
            notes: notes.trim() || null,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to record usage');
      }

      const data = await response.json();

      setSuccess(
        `Successfully recorded ${data.outreaches_used} outreaches. ` +
        `Balance: ${data.balance_before} → ${data.balance_after}`
      );

      // Reset form
      setOutreachesUsed('');
      setSelectedCampaignId('');
      setNotes('');

      // Refresh user data to update balance
      const updatedUsers = users.map(u => {
        if (u.id === selectedUserId) {
          return {
            ...u,
            outreach_credits_balance: data.balance_after,
          };
        }
        return u;
      });
      setUsers(updatedUsers);

      // Scroll to success message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error recording usage:', error);
      setError(error instanceof Error ? error.message : 'Failed to record usage');
    } finally {
      setSubmitting(false);
    }
  };

  const selectedUser = users.find(u => u.id === selectedUserId);

  const getPlanLabel = (user: User): string => {
    if (user.pricing_plan === 'platform') return 'Platform';
    if (user.pricing_plan === 'usage_based') return 'Usage-Based';
    if (user.billing_status === 'trial') return 'Trial';
    return 'Free';
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-8"></div>
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
            <div className="h-10 bg-gray-200 rounded mb-4"></div>
            <div className="h-10 bg-gray-200 rounded mb-4"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Record Outreach Usage</h1>
        <p className="text-lg text-gray-600 mt-2">Record outreach usage for users with outreach credits</p>
      </div>

      {/* Success Message */}
      {success && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-green-800 font-medium">✅ {success}</p>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800 font-medium">❌ {error}</p>
        </div>
      )}

      {users.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border p-12 text-center">
          <div className="text-5xl mb-4">👥</div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Users Found</h3>
          <p className="text-gray-600">
            No users with outreach credits in your assigned companies.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <form onSubmit={handleSubmit}>
            {/* User Selection */}
            <div className="mb-6">
              <label htmlFor="user" className="block text-sm font-medium text-gray-700 mb-2">
                Select User <span className="text-red-500">*</span>
              </label>
              <select
                id="user"
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-purple-600"
                required
              >
                <option value="">-- Select a user --</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.full_name || user.email} ({user.companies.name}) [{getPlanLabel(user)}] - {user.outreach_credits_balance} credits
                  </option>
                ))}
              </select>
            </div>

            {/* Selected User Info */}
            {selectedUser && (
              <div className="mb-6 bg-purple-50 border border-purple-200 rounded-lg p-4">
                <h3 className="font-semibold text-purple-900 mb-2">User Information</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-purple-700">Name:</span>
                    <span className="ml-2 text-purple-900 font-medium">
                      {selectedUser.full_name || 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="text-purple-700">Email:</span>
                    <span className="ml-2 text-purple-900 font-medium">{selectedUser.email}</span>
                  </div>
                  <div>
                    <span className="text-purple-700">Company:</span>
                    <span className="ml-2 text-purple-900 font-medium">
                      {selectedUser.companies.name}
                    </span>
                  </div>
                  <div>
                    <span className="text-purple-700">Available Credits:</span>
                    <span className="ml-2 text-purple-900 font-bold">
                      {selectedUser.outreach_credits_balance}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Outreaches Used */}
            <div className="mb-6">
              <label htmlFor="outreaches" className="block text-sm font-medium text-gray-700 mb-2">
                Outreaches Used <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                id="outreaches"
                value={outreachesUsed}
                onChange={(e) => setOutreachesUsed(e.target.value)}
                min="1"
                max="1000"
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-purple-600"
                placeholder="Enter number of outreaches"
                required
              />
              <p className="text-sm text-gray-500 mt-1">Maximum: 1000 per entry</p>
            </div>

            {/* Campaign Selection (Optional) — Epoch 75: Searchable dropdown */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Campaign (Optional)
              </label>
              <CampaignSearchSelect
                campaigns={campaigns}
                value={selectedCampaignId}
                onChange={setSelectedCampaignId}
                placeholder="-- Select a campaign (optional) --"
              />
            </div>

            {/* Notes */}
            <div className="mb-6">
              <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
                Notes (Optional)
              </label>
              <textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-purple-600"
                placeholder="Add any relevant notes about this usage (e.g., 'LinkedIn connection requests sent via Dripify')"
              />
            </div>

            {/* Submit Button */}
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => navigate('/csr-campaigns')}
                className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className={`px-6 py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors ${
                  submitting ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {submitting ? 'Recording...' : 'Record Usage'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Info Box */}
      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="font-semibold text-blue-900 mb-2">💡 Usage Recording Guidelines</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Each recorded outreach deducts 1 credit from the user's balance</li>
          <li>• Users must have sufficient credits available before recording</li>
          <li>• Maximum 1000 outreaches per entry (record multiple entries if needed)</li>
          <li>• Include campaign linkage when possible for better tracking</li>
          <li>• Add detailed notes to help users understand the usage</li>
          <li>• All usage is logged with CSR attribution for audit trail</li>
        </ul>
      </div>
    </div>
  );
};

export default CSRRecordUsage;
